import { inject } from '@angular/core';
import { ProductPromotionsApi, Promotion } from '@idealsupply/products-api-angular-client';
import { Prettify, type } from '@ngrx/signals';
import { APP_CONFIG } from 'config-data';
import { map } from 'rxjs';
import { entityFeature, PartialListResult } from 'state-data';

/** Promotion enriched with a thumbnail URL. */
export type PromotionEntity = Promotion & { thumbnail: string };

// ---------------------------------------------------------------------------
// Feature
// TEnriched = PromotionEntity (Promotion + thumbnail URL)
// TEntity   = Promotion        (raw API response)
// convert adds thumbnailUrl at view time
// ---------------------------------------------------------------------------

const promotionFeature = entityFeature({
  name: 'promotion',
  entity: type<PromotionEntity>(),
  record: type<Promotion>(),
  id: type<string>(),
  selectId: (p) => p.id,
  convert: () => {
    const config = inject(APP_CONFIG);
    const baseUrl = (config.auth?.apiRoot ?? `https://api.idealsupply.com`) + '/products/v1';
    return (p) =>
      ({
        ...p,
        thumbnail: `${baseUrl}/promotions/${p.id}/thumbnail`,
      }) as PromotionEntity;
  },
});

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const PromotionStore = promotionFeature.store(
  { providedIn: 'root' },

  promotionFeature.base({ protect: { entities: true } }),

  promotionFeature.getById({
    load: () => {
      const api = inject(ProductPromotionsApi);
      return (promotionId: string) => api.getPromotionDetail({ promotionId });
    },
    cacheSeconds: 300,
  }),

  promotionFeature.list({
    load: () => {
      const api = inject(ProductPromotionsApi);
      return (skip: number, limit: number) =>
        api
          .getPromotions({ skip, limit })
          .pipe(
            map(
              (r) =>
                ({ more: r.more ?? false, data: r.data, limit: r.limit ?? limit, skip: r.skip ?? skip }) as PartialListResult<Promotion>,
            ),
          );
    },
    cacheSeconds: 300,
    initialLimit: 25,
  }),
);

export type PromotionStore = Prettify<InstanceType<typeof PromotionStore>>;
